import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Plus, Grid3x3, Package } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ModelBOM() {
  const [models, setModels] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [rows, setRows] = useState([]);
  const [newRow, setNewRow] = useState({ item_id: "", qty_per_bottle: "", process_id: "" });

  const loadBom = useCallback((model) => {
    if (!model) { setRows([]); return; }
    api.get("/model-bom", { params: { model_name: model } }).then((r) => setRows(r.data));
  }, []);

  useEffect(() => {
    Promise.all([api.get("/models"), api.get("/inventory-items"), api.get("/processes")]).then(([m, inv, p]) => {
      setModels(m.data); setInventory(inv.data); setProcesses(p.data);
      if (m.data.length > 0) setSelectedModel(m.data[0].name);
    });
  }, []);

  useEffect(() => { loadBom(selectedModel); }, [selectedModel, loadBom]);

  const addComponent = async () => {
    if (!selectedModel) { toast.error("Select a model"); return; }
    if (!newRow.item_id) { toast.error("Select a component"); return; }
    try {
      await api.post("/model-bom", { model_name: selectedModel, item_id: newRow.item_id, qty_per_bottle: +newRow.qty_per_bottle || 0, process_id: newRow.process_id || null });
      toast.success("Component saved"); setNewRow({ item_id: "", qty_per_bottle: "", process_id: "" }); loadBom(selectedModel);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveQty = async (row, qty) => {
    try {
      await api.post("/model-bom", { model_name: selectedModel, item_id: row.item_id, qty_per_bottle: +qty || 0, process_id: row.process_id || null });
      toast.success("Updated"); loadBom(selectedModel);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveProcess = async (row, pid) => {
    try {
      await api.post("/model-bom", { model_name: selectedModel, item_id: row.item_id, qty_per_bottle: row.qty_per_bottle, process_id: pid || null });
      toast.success("Process set"); loadBom(selectedModel);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    try { await api.delete(`/model-bom/${id}`); toast.success("Removed"); loadBom(selectedModel); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const usedIds = new Set(rows.map((r) => r.item_id));
  const available = inventory.filter((i) => !usedIds.has(i.id));

  return (
    <Layout title="Model BOM" subtitle="Per-bottle component recipe — pulled from Inventory Items">
      <Toaster richColors position="top-right" />
      <div className="section-card mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">Select Product Model</label>
        <select data-testid="mbom-model-select" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} className="w-full md:w-96 border border-slate-300 rounded-lg px-3 py-2 text-sm">
          {models.length === 0 && <option value="">No models — create one first</option>}
          {models.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
      </div>

      <div className="section-card overflow-x-auto">
        <div className="flex items-center gap-2 mb-3"><Grid3x3 size={18} className="text-blue-600" /><h3 className="font-heading font-semibold">Components for {selectedModel || "—"} (per 1 bottle)</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left py-3 px-3">Component</th>
              <th className="text-left py-3 px-3">Unit</th>
              <th className="text-right py-3 px-3">Qty / Bottle</th>
              <th className="text-left py-3 px-3">Consumed At (process)</th>
              <th className="text-right py-3 px-3">In Stock</th>
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="mbom-table-body">
            {rows.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">No components yet. Add Coil, Lid, Packing Cover, Gift Box, Master Box.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                <td className="py-2 px-3 font-medium flex items-center gap-2"><Package size={14} className="text-slate-400" />{r.item_name}</td>
                <td className="py-2 px-3"><span className="stat-pill bg-slate-100 text-slate-700">{r.base_unit}</span></td>
                <td className="py-1 px-3"><input data-testid={`mbom-qty-${r.item_id}`} type="number" step="0.0001" defaultValue={r.qty_per_bottle} onBlur={(e) => { if (+e.target.value !== r.qty_per_bottle) saveQty(r, e.target.value); }} className="w-28 ml-auto block border border-slate-300 rounded px-2 py-1 text-right text-sm" /></td>
                <td className="py-1 px-3">
                  <select data-testid={`mbom-proc-${r.item_id}`} value={r.process_id || ""} onChange={(e) => saveProcess(r, e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
                    <option value="">Final stage (default)</option>
                    {processes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </td>
                <td className="py-2 px-3 text-right font-mono text-slate-600">{r.current_stock?.toLocaleString()} {r.base_unit}</td>
                <td className="py-2 px-3 text-right"><ConfirmDelete testId={`mbom-delete-${r.item_id}`} onConfirm={() => onDelete(r.id)} title="Remove component from BOM?" /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50/60">
              <td className="py-2 px-3">
                <select data-testid="mbom-new-item" value={newRow.item_id} onChange={(e) => setNewRow({ ...newRow, item_id: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
                  <option value="">+ Add component…</option>
                  {available.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.base_unit})</option>)}
                </select>
              </td>
              <td></td>
              <td className="py-1 px-3"><input data-testid="mbom-new-qty" type="number" step="0.0001" value={newRow.qty_per_bottle} onChange={(e) => setNewRow({ ...newRow, qty_per_bottle: e.target.value })} placeholder="qty/bottle" className="w-28 ml-auto block border border-slate-300 rounded px-2 py-1 text-right text-sm" /></td>
              <td className="py-1 px-3">
                <select data-testid="mbom-new-proc" value={newRow.process_id} onChange={(e) => setNewRow({ ...newRow, process_id: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
                  <option value="">Final stage (default)</option>
                  {processes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </td>
              <td></td>
              <td className="py-2 px-3 text-right"><button data-testid="mbom-add-btn" onClick={addComponent} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-semibold flex items-center gap-1 ml-auto"><Plus size={14} /> Add</button></td>
            </tr>
          </tfoot>
        </table>
        <p className="text-xs text-slate-500 mt-3">Example (1000ml): Coil 0.167 kg, Lid 1, Packing Cover 1, Gift Box 1, Master Box 0.0417 (1/24). Coil is deducted at the pipe stage; other components at the final stage.</p>
      </div>
    </Layout>
  );
}
